import React from "react";
import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useErp } from "@/context/ErpContext";
import api from "@/lib/api";
import {
  ChartLine, Vault, HandCoins, ListBullets, CurrencyCircleDollar,
  Bank, ArrowsClockwise, SignOut, ShieldCheck, PlugsConnected,
  Certificate, Scroll, Sparkle, CaretDown, List, X,
  FileText, UserPlus, Clock, Users, BookOpen, Receipt,
  UserCircle, MagnifyingGlass, PlusCircle,
  ClipboardText, ShoppingCart, Megaphone, ArrowsLeftRight,
  Wallet, Package, MapPin, ListChecks,
  Cube, Buildings, Truck, ClipboardText as ClipboardIcon,
  Wrench, ArrowsOut, Coins, Archive,
  Gear,
  Brain,
  Database,
  ShieldWarning,
  ChartLineUp,
  MapTrifold,
  Broadcast,
  Lightning,
  Warning,
  Sun,
  Moon,
  Calculator,
  TrendUp,
  Buildings as BuildingsIcon,
  UsersThree,
  Files,
  SquaresFour,
  Drop,
  Tray,
  LockKey,
} from "@phosphor-icons/react";
import ProfileSetupModal from "@/components/erp/ProfileSetupModal";
import ChangePasswordModal from "@/components/erp/ChangePasswordModal";
import AgentRunModal from "@/components/erp/AgentRunModal";
import CommandPalette from "@/components/layout/CommandPalette";

// Apply persisted theme as early as possible so we avoid a light-flash
// on first paint after a hard reload.
const applyStoredTheme = () => {
  try {
    const v = localStorage.getItem("theme");
    document.documentElement.classList.toggle("dark", v === "dark");
  } catch { /* ignore */ }
};
applyStoredTheme();

function useTheme() {
  const [theme, setTheme] = React.useState(() => {
    try { return localStorage.getItem("theme") === "dark" ? "dark" : "light"; } catch { return "light"; }
  });
  React.useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    try { localStorage.setItem("theme", theme); } catch { /* ignore */ }
  }, [theme]);
  return [theme, () => setTheme((t) => (t === "dark" ? "light" : "dark"))];
}

const NAV = [
  { to: "/dashboard", label: "Dashboard", icon: ChartLine, testid: "nav-dashboard" },
  { to: "/fixed-deposits", label: "Fixed Deposits", icon: Vault, testid: "nav-fds" },
  { to: "/loans", label: "Loans", icon: HandCoins, testid: "nav-loans" },
  { to: "/bank-guarantees", label: "Bank Guarantees", icon: Certificate, testid: "nav-bg" },
  { to: "/letters-of-credit", label: "Letters of Credit", icon: Scroll, testid: "nav-lc" },
  { to: "/transactions", label: "Transactions", icon: ListBullets, testid: "nav-txns" },
  { to: "/fx", label: "FX & MTM", icon: CurrencyCircleDollar, testid: "nav-fx" },
  { to: "/bank-accounts", label: "Bank Accounts", icon: Bank, testid: "nav-banks" },
  { to: "/reconciliation", label: "Reconciliation", icon: ArrowsClockwise, testid: "nav-reco" },
  { to: "/approvals", label: "Approvals", icon: ShieldCheck, testid: "nav-approvals" },
];

const ALM_NAV = [
  { to: "/alm/dashboard", label: "ALM Dashboard", icon: ChartLine, testid: "nav-alm-dashboard" },
  { to: "/alm/map", label: "Live Asset Map", icon: MapTrifold, testid: "nav-alm-map" },
  { to: "/alm/products", label: "Product Master", icon: Cube, testid: "nav-alm-products" },
  { to: "/alm/sites", label: "Site Master", icon: Buildings, testid: "nav-alm-sites" },
  { to: "/alm/locations", label: "Location Master", icon: MapPin, testid: "nav-alm-locations" },
  { to: "/alm/procurement-plans", label: "Plan to Procure", icon: ClipboardIcon, testid: "nav-alm-pp" },
  { to: "/alm/receipts", label: "Receiving Process", icon: Truck, testid: "nav-alm-receipts" },
  { to: "/alm/assets", label: "Asset Tracking", icon: Package, testid: "nav-alm-assets" },
  { to: "/alm/movements", label: "Movement of Items", icon: ArrowsOut, testid: "nav-alm-movements" },
  { to: "/alm/capitalizations", label: "Asset Capitalization", icon: Coins, testid: "nav-alm-cap" },
  { to: "/alm/maintenance", label: "Asset Maintenance", icon: Wrench, testid: "nav-alm-maint" },
  { to: "/alm/retirements", label: "Asset Retirement", icon: Archive, testid: "nav-alm-ret" },
];

const AGENT_ICONS = {
  "invoice-create": FileText,
  "supplier-create": UserPlus,
  "invoice-ageing": Clock,
  "supplier-ageing": Users,
  "journal-create": BookOpen,
  "receipt-create": Receipt,
  "customer-create": UserCircle,
  "get-hr": MagnifyingGlass,
  "create-hr": PlusCircle,
  "purchase-req-create": ClipboardText,
  "purchase-order-create": ShoppingCart,
  "rfp-create": Megaphone,
  "bank-transfer": ArrowsLeftRight,
  "bank-balance": Wallet,
  "asset-addition": Package,
  "asset-transfer": MapPin,
  "asset-reconcile": ListChecks,
  "sales-order-booking": ShoppingCart,
  // Finance Efficiency agents
  "finance-account-overview": BookOpen,
  "finance-anomaly": MagnifyingGlass,
  "finance-variance": ChartLine,
  "finance-future-insight": Sparkle,
  "finance-narrative": FileText,
  "finance-monthend": ListChecks,
};

export default function AppLayout({ children }) {
  const { user, logout } = useAuth();
  const { launchAgent, agents, setSetupOpen } = useErp();
  const nav = useNavigate();
  const { pathname } = useLocation();
  const [theme, toggleTheme] = useTheme();

  // Sidebar group expand/collapse — collapsed by default, persisted to
  // localStorage so the user's choice survives reloads & navigation.
  const useGroup = (key) => {
    const [open, setOpen] = React.useState(() => {
      try { return localStorage.getItem(`sb:${key}`) === "1"; } catch { return false; }
    });
    const toggle = React.useCallback(() => {
      setOpen((o) => {
        const next = !o;
        try { localStorage.setItem(`sb:${key}`, next ? "1" : "0"); } catch { /* ignore */ }
        return next;
      });
    }, [key]);
    return [open, toggle];
  };
  const [erpOpen, toggleErp] = useGroup("erp");
  const [finOpen, toggleFin] = useGroup("fin");
  const [almOpen, toggleAlm] = useGroup("alm");
  const [treasuryOpen, toggleTreasury] = useGroup("treasury");
  const [adminOpen, toggleAdmin] = useGroup("admin");
  const [p2pOpen, toggleP2p] = useGroup("p2p");
  const [epmOpen, toggleEpm] = useGroup("epm");
  const [epbcsOpen, toggleEpbcs] = useGroup("epbcs");
  const [camOpen, toggleCam] = useGroup("cam");
  const [reportingOpen, toggleReporting] = useGroup("reporting");
  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [pwOpen, setPwOpen] = React.useState(false);
  const [paletteOpen, setPaletteOpen] = React.useState(false);
  const [taskCount, setTaskCount] = React.useState(0);

  React.useEffect(() => {
    let alive = true;
    api.get("/my-tasks").then((r) => { if (alive) setTaskCount(r.data.count || 0); }).catch(() => {});
    return () => { alive = false; };
  }, [pathname]);

  React.useEffect(() => {
    if (pathname.startsWith("/planning") && !epbcsOpen) toggleEpbcs();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const closeMobile = () => setMobileOpen(false);

  // Dynamic agent catalog (filtered server-side per tenant/user)
  const coreAgents = agents.filter((a) => a.category === "core");
  const financeAgents = agents.filter((a) => a.category === "finance_efficiency");
  const isProductAdmin = user?.role === "product_admin";

  // Main menu visibility — defaults are permissive so legacy users (without the
  // field) still see everything they used to.
  const ALL_MENUS = ["admin", "erp_agents", "finance_efficiency", "telecom", "treasury"];
  const allowedMenus = Array.isArray(user?.allowed_menus) && user.allowed_menus.length > 0
    ? user.allowed_menus
    : ALL_MENUS;
  const canSee = (k) => allowedMenus.includes(k);

  const navItemClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium rounded-md transition-colors mx-2 ${
      isActive
        ? "bg-[#2563EB] text-white"
        : "text-gray-300 hover:bg-white/[0.06] hover:text-white"
    }`;

  return (
    <div className="min-h-screen bg-[#F5F7FB]">
      {/* Mobile top bar */}
      <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between border-b border-[#1F2A40] bg-[#1A2238] px-4 h-14 text-white">
        <Link to="/dashboard" className="flex items-center gap-2" data-testid="app-mobile-brand">
          <img src="/vaku-logo.png" alt="Vaku" className="h-11 w-11 rounded-lg object-cover" />
          <span className="font-black text-base tracking-tight text-white">Vaku</span>
        </Link>
        <button onClick={() => setMobileOpen(true)} data-testid="app-mobile-menu-toggle" aria-label="Open menu" className="text-gray-200">
          <List size={24} weight="bold" />
        </button>
      </header>

      {/* Backdrop (mobile only) */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={closeMobile} data-testid="app-sidebar-backdrop" />
      )}

      <div className="lg:flex">
        {/* Sidebar — Zoho-inspired dark navy theme */}
        <aside
          data-testid="app-sidebar"
          className={`fixed lg:static inset-y-0 left-0 z-50 w-64 lg:w-64 shrink-0 bg-[#1A2238] text-gray-200 flex flex-col transition-transform duration-200 ease-out lg:translate-x-0 ${mobileOpen ? "translate-x-0" : "-translate-x-full"}`}
        >
          <div className="px-5 py-4 border-b border-white/10 flex items-center justify-between">
            <Link to="/dashboard" className="flex items-center gap-3" data-testid="brand-link" onClick={closeMobile}>
              <img src="/vaku-logo.png" alt="Vaku" className="h-10 w-10 rounded-lg object-cover" />
              <div>
                <div className="text-[18px] font-bold tracking-tight text-white leading-none">Vaku</div>
                <div className="text-[9px] tracking-[0.18em] uppercase text-gray-400 mt-1">the only AI</div>
              </div>
            </Link>
            <button onClick={closeMobile} className="lg:hidden text-gray-300" data-testid="app-sidebar-close" aria-label="Close menu">
              <X size={20} weight="bold" />
            </button>
          </div>
          <div className="px-3 pt-3 space-y-1.5">
            <button
              onClick={() => { closeMobile(); setPaletteOpen(true); }}
              data-testid="command-palette-trigger"
              className="w-full flex items-center gap-2 px-3 py-2 rounded-md border border-white/15 text-gray-400 hover:text-white hover:border-white/30 text-[12px] transition-colors"
            >
              <MagnifyingGlass size={14} weight="bold" />
              <span className="flex-1 text-left">Search…</span>
              <kbd className="text-[9px] font-bold tracking-wider border border-white/20 rounded px-1 py-0.5">Ctrl K</kbd>
            </button>
            <NavLink to="/my-tasks" data-testid="nav-my-tasks" onClick={closeMobile}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2 rounded-md text-[12px] font-semibold transition-colors ${
                  isActive ? "bg-[#2563EB] text-white" : "text-gray-300 hover:bg-white/[0.06] hover:text-white"}`}>
              <Tray size={14} weight="bold" />
              <span className="flex-1 text-left">My Tasks</span>
              {taskCount > 0 && (
                <span className="inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-[#C8A24B] text-[#0B1E3F] text-[10px] font-black"
                  data-testid="my-tasks-badge">{taskCount}</span>
              )}
            </NavLink>
          </div>
          <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
            {/* 0) Admin Console group — product_admin only */}
            {isProductAdmin && canSee("admin") && (
              <div className="pb-3 mb-2 border-b border-white/10">
                <button
                  onClick={toggleAdmin}
                  data-testid="admin-group-toggle"
                  className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
                >
                  <span className="flex items-center gap-2">
                    <Gear size={14} weight="fill" className="text-amber-400" /> Admin Console
                  </span>
                  <CaretDown size={12} weight="bold" className={`transition-transform ${adminOpen ? "" : "-rotate-90"}`} />
                </button>
                {adminOpen && (
                  <div className="mt-1 space-y-0.5" data-testid="admin-group">
                    <NavLink to="/admin/agent-setup" data-testid="nav-admin-agent-setup"
                      className={navItemClass} onClick={closeMobile}>
                      <Sparkle size={16} weight="bold" />
                      <span>Agent Setup</span>
                    </NavLink>
                    <NavLink to="/admin/users" data-testid="nav-admin-users"
                      className={navItemClass} onClick={closeMobile}>
                      <Users size={16} weight="bold" />
                      <span>Users & Access</span>
                    </NavLink>
                    <NavLink to="/admin/llm-settings" data-testid="nav-admin-llm-settings"
                      className={navItemClass} onClick={closeMobile}>
                      <Brain size={16} weight="bold" />
                      <span>LLM Settings</span>
                    </NavLink>
                    {canSee("vakuai_db") && (
                      <NavLink to="/admin/database" data-testid="nav-admin-database"
                        className={navItemClass} onClick={closeMobile}>
                        <Database size={16} weight="bold" />
                        <span>Vakuai Database</span>
                      </NavLink>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* 1) ERP AI Agents group (first) */}
            {canSee("erp_agents") && (
            <div>
              <button
                onClick={toggleErp}
                data-testid="erp-agents-group-toggle"
                className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <Sparkle size={14} weight="fill" className="text-amber-400" /> ERP AI Agents
                </span>
                <CaretDown size={12} weight="bold" className={`transition-transform ${erpOpen ? "" : "-rotate-90"}`} />
              </button>
              {erpOpen && (
                <div className="mt-1 space-y-0.5" data-testid="erp-agents-group">
                  {coreAgents.map(({ key, label }) => {
                    const Icon = AGENT_ICONS[key] || Sparkle;
                    return (
                      <button
                        key={key}
                        data-testid={`agent-${key}`}
                        onClick={() => { closeMobile(); launchAgent(key); }}
                        className="w-full flex items-center gap-3 px-3 py-2 mx-2 rounded-md text-[13px] font-medium text-gray-300 hover:bg-white/[0.06] hover:text-white transition-colors text-left"
                        title={label}
                      >
                        <Icon size={16} weight="bold" className="shrink-0 text-gray-400" />
                        <span className="truncate">{label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* P2P Compliance group */}
            {canSee("p2p_compliance") && (
            <div className="pt-4 mt-2 border-t border-white/10" data-testid="p2p-group">
              <button
                onClick={toggleP2p}
                data-testid="p2p-group-toggle"
                className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <ShieldWarning size={14} weight="fill" className="text-amber-400" /> Procure to Pay
                </span>
                <CaretDown size={12} weight="bold" className={`transition-transform ${p2pOpen ? "" : "-rotate-90"}`} />
              </button>
              {p2pOpen && (
                <div className="mt-1 space-y-0.5">
                  <NavLink to="/p2p-compliance" data-testid="nav-p2p-compliance"
                    className={navItemClass} onClick={closeMobile}>
                    <ShieldWarning size={16} weight="bold" />
                    <span>Compliance Agent</span>
                  </NavLink>
                </div>
              )}
            </div>
            )}

            {/* EPM group */}
            {canSee("epm") && (
            <div className="pt-4 mt-2 border-t border-white/10" data-testid="epm-group">
              <button
                onClick={toggleEpm}
                data-testid="epm-group-toggle"
                className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <ChartLineUp size={14} weight="fill" className="text-amber-400" /> EPM
                </span>
                <CaretDown size={12} weight="bold" className={`transition-transform ${epmOpen ? "" : "-rotate-90"}`} />
              </button>
              {epmOpen && (
                <div className="mt-1 space-y-0.5">
                  <NavLink to="/epm/consolidation" data-testid="nav-epm-consolidation"
                    className={navItemClass} onClick={closeMobile}>
                    <ChartLineUp size={16} weight="bold" />
                    <span>Entity Consolidation</span>
                  </NavLink>
                </div>
              )}
            </div>
            )}

            {/* Planning & Budgeting (EPBCS) group */}
            {canSee("epbcs") && (
            <div className="pt-4 mt-2 border-t border-white/10" data-testid="epbcs-group">
              <button
                onClick={toggleEpbcs}
                data-testid="epbcs-group-toggle"
                className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <Calculator size={14} weight="fill" className="text-amber-400" /> Planning &amp; Budgeting
                </span>
                <CaretDown size={12} weight="bold" className={`transition-transform ${epbcsOpen ? "" : "-rotate-90"}`} />
              </button>
              {epbcsOpen && (
                <div className="mt-1 space-y-0.5">
                  {[
                    ["setup", "Setup & Masters", Gear, [
                      ["masters", "Dimension Masters"], ["cycle", "Budget Cycle"],
                      ["matrix", "Role Matrix"], ["users", "User Access"], ["import", "Data Import"],
                      ["drivers", "Driver Setup"]]],
                    ["revenue", "Revenue Budgeting", TrendUp, [
                      ["other", "Other Revenue"], ["driver", "Driver Based"], ["contract", "Contract Based"],
                      ["product", "Product Based"], ["hosp1", "Hospitality (CY Forecast)"], ["hosp2", "Hospitality (Budget Year)"]]],
                    ["opex", "OpEx Budgeting", Coins, [
                      ["contract", "Contract Based"], ["driver", "Driver Based"], ["other", "Other Expenses"],
                      ["hosp1", "Hospitality (CY Forecast)"], ["hosp2", "Hospitality (Budget Year)"],
                      ["material", "Material Cost"], ["bom", "Material BOM Master"],
                      ["transport", "Transportation Setup"],
                      ["fuel", "Fuel Cost"], ["fleet", "Transportation Fleet"]]],
                    ["capex", "CapEx & Depreciation", BuildingsIcon, [
                      ["wbs_new", "New Projects"], ["wbs_existing", "Existing Projects"],
                      ["planning", "Capital Planning"], ["assets_new", "New Asset Procurement"],
                      ["assets_existing", "Existing Assets"],
                      ["combos", "Valid Combinations"], ["review", "Review"]]],
                    ["workforce", "Workforce Budgeting", UsersThree, [
                      ["increment", "Increment Assumptions"], ["attrition", "Attrition / Notice Period"],
                      ["existing", "Existing Employees"], ["hires", "New Hires"],
                      ["scales", "Pay Scales"], ["review", "Review"]]],
                    ["financials", "Budgeted Financials", Files, [
                      ["income", "Income Statement"], ["balance", "Balance Sheet"],
                      ["schedule3", "Schedule III Reports (Actuals)"],
                      ["approvals", "Approval Workflow"],
                      ["workflow", "Workflow Setup"], ["sla", "SLA & Notifications"]]],
                    ["dashboards", "Dashboards", SquaresFour, []],
                  ].map(([key, label, Icon, subs]) => {
                    const sectionActive = pathname === `/planning/${key}` || pathname.startsWith(`/planning/${key}/`);
                    const currentSub = sectionActive ? (pathname.split("/")[3] || "") : "";
                    return (
                      <div key={key}>
                        <button
                          data-testid={`nav-epbcs-${key}`}
                          onClick={() => { closeMobile(); nav(`/planning/${key}`); }}
                          className={`w-full flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium transition-colors mx-2 text-left border-l-2 ${
                            sectionActive
                              ? "border-[#C8A24B] bg-white/[0.08] text-white"
                              : "border-transparent text-gray-300 hover:bg-white/[0.06] hover:text-white"
                          }`}>
                          <Icon size={16} weight="bold" />
                          <span className="flex-1">{label}</span>
                          {subs.length > 0 && (
                            <CaretDown size={11} weight="bold"
                              className={`transition-transform ${sectionActive ? "" : "-rotate-90"} text-gray-500`} />
                          )}
                        </button>
                        {sectionActive && subs.length > 0 && (
                          <div className="mt-0.5 mb-1 space-y-0.5">
                            {subs.map(([sk, sl], i) => {
                              const subActive = currentSub === sk || (!currentSub && i === 0);
                              return (
                                <NavLink key={sk} to={`/planning/${key}/${sk}`}
                                  data-testid={`nav-epbcs-${key}-${sk}`} onClick={closeMobile}
                                  className={`flex items-center gap-2 ml-9 mr-2 px-3 py-1.5 text-[12px] transition-colors border-l ${
                                    subActive
                                      ? "border-[#C8A24B] bg-white/[0.06] text-[#C8A24B] font-semibold"
                                      : "border-white/10 text-gray-400 hover:bg-white/[0.06] hover:text-white"
                                  }`}>
                                  <span className="truncate">{sl}</span>
                                </NavLink>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            )}

            {/* Commodity Asset Management group */}
            {canSee("commodity") && (
            <div className="pt-4 mt-2 border-t border-white/10" data-testid="cam-group">
              <button
                onClick={toggleCam}
                data-testid="cam-group-toggle"
                className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <Drop size={14} weight="fill" className="text-amber-400" /> Commodity Asset Mgmt
                </span>
                <CaretDown size={12} weight="bold" className={`transition-transform ${camOpen ? "" : "-rotate-90"}`} />
              </button>
              {camOpen && (
                <div className="mt-1 space-y-0.5">
                  {[
                    ["dashboard", "Dashboard", SquaresFour],
                    ["deals", "Trade Capture", ArrowsLeftRight],
                    ["movements", "Scheduling & Logistics", Truck],
                    ["inventory", "Inventory", Package],
                    ["reconciliation", "Reconciliation", ListChecks],
                    ["masters", "Setup & Masters", Gear],
                  ].map(([key, label, Icon]) => (
                    <NavLink key={key} to={`/commodity/${key}`}
                      data-testid={`nav-cam-${key}`} onClick={closeMobile}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium transition-colors mx-2 border-l-2 ${
                          isActive
                            ? "border-[#C8A24B] bg-white/[0.08] text-white"
                            : "border-transparent text-gray-300 hover:bg-white/[0.06] hover:text-white"
                        }`}>
                      <Icon size={16} weight="bold" />
                      <span>{label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* Reporting Agent group */}
            {canSee("reporting_agent") && (
            <div className="pt-4 mt-2 border-t border-white/10" data-testid="reporting-group">
              <button
                onClick={toggleReporting}
                data-testid="reporting-group-toggle"
                className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <FileText size={14} weight="fill" className="text-amber-400" /> Reporting Agent
                </span>
                <CaretDown size={12} weight="bold" className={`transition-transform ${reportingOpen ? "" : "-rotate-90"}`} />
              </button>
              {reportingOpen && (
                <div className="mt-1 space-y-0.5">
                  {[
                    ["ai", "Ask AI", Sparkle],
                    ["catalog", "Report Catalog", ListBullets],
                    ["queries", "Report Queries", Database],
                    ["connection", "Fusion Connection", PlugsConnected],
                  ].map(([key, label, Icon]) => (
                    <NavLink key={key} to={`/reporting/${key}`}
                      data-testid={`nav-reporting-${key}`} onClick={closeMobile}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 text-[13px] font-medium transition-colors mx-2 border-l-2 ${
                          isActive
                            ? "border-[#C8A24B] bg-white/[0.08] text-white"
                            : "border-transparent text-gray-300 hover:bg-white/[0.06] hover:text-white"
                        }`}>
                      <Icon size={16} weight="bold" />
                      <span>{label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* 2) Finance Efficiency agents (GL + subledger drill-down) */}
            {canSee("finance_efficiency") && (
            <div className="pt-4 mt-2 border-t border-white/10">
              <button
                onClick={toggleFin}
                data-testid="finance-eff-group-toggle"
                className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <ChartLine size={14} weight="fill" className="text-amber-400" /> Finance Efficiency
                </span>
                <CaretDown size={12} weight="bold" className={`transition-transform ${finOpen ? "" : "-rotate-90"}`} />
              </button>
              {finOpen && (
                <div className="mt-1 space-y-0.5" data-testid="finance-eff-group">
                  {financeAgents.map(({ key, label }) => {
                    const Icon = AGENT_ICONS[key] || Sparkle;
                    return (
                      <button
                        key={key}
                        data-testid={`agent-${key}`}
                        onClick={() => { closeMobile(); launchAgent(key); }}
                        className="w-full flex items-center gap-3 px-3 py-2 mx-2 rounded-md text-[13px] font-medium text-gray-300 hover:bg-white/[0.06] hover:text-white transition-colors text-left"
                        title={label}
                      >
                        <Icon size={16} weight="bold" className="shrink-0 text-gray-400" />
                        <span className="truncate">{label}</span>
                      </button>
                    );
                  })}
                  <NavLink
                    to="/automations"
                    data-testid="nav-automations"
                    className={navItemClass}
                    onClick={closeMobile}
                  >
                    <ArrowsClockwise size={16} weight="bold" />
                    <span>Automations</span>
                  </NavLink>
                </div>
              )}
            </div>
            )}

            {/* 3) Telecom Solution for ERP group — currently houses ALM; more telecom solutions can be added here */}
            {canSee("telecom") && (
            <div className="pt-4 mt-2 border-t border-white/10">
              <button
                onClick={toggleAlm}
                data-testid="telecom-group-toggle"
                className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <Broadcast size={14} weight="fill" className="text-amber-400" /> Telecom Solution
                </span>
                <CaretDown size={12} weight="bold" className={`transition-transform ${almOpen ? "" : "-rotate-90"}`} />
              </button>
              {almOpen && (
                <div className="mt-1 space-y-0.5" data-testid="telecom-group">
                  {/* Finance × BSS + Revenue Assurance + Spectrum + Slicing */}
                  <div className="px-3 pt-2 pb-1 text-[9px] tracking-[0.18em] uppercase font-bold text-gray-400">
                    AI Insights
                  </div>
                  <NavLink to="/telecom/finance-bss" data-testid="nav-telecom-finance-bss" className={navItemClass} onClick={closeMobile}>
                    <ChartLine size={16} weight="bold" /><span>Finance × BSS</span>
                  </NavLink>
                  <NavLink to="/telecom/revenue-assurance" data-testid="nav-telecom-ra" className={navItemClass} onClick={closeMobile}>
                    <Warning size={16} weight="bold" /><span>Revenue Assurance</span>
                  </NavLink>
                  <NavLink to="/telecom/spectrum" data-testid="nav-telecom-spectrum" className={navItemClass} onClick={closeMobile}>
                    <Broadcast size={16} weight="bold" /><span>Spectrum & Licences</span>
                  </NavLink>
                  <NavLink to="/telecom/slicing" data-testid="nav-telecom-slicing" className={navItemClass} onClick={closeMobile}>
                    <Lightning size={16} weight="bold" /><span>5G Slicing Billing</span>
                  </NavLink>

                  <div className="px-3 pt-3 pb-1 text-[9px] tracking-[0.18em] uppercase font-bold text-gray-400">
                    Asset Lifecycle (ALM)
                  </div>
                  {ALM_NAV.map(({ to, label, icon: Icon, testid }) => (
                    <NavLink key={to} to={to} data-testid={testid} className={navItemClass} onClick={closeMobile}>
                      <Icon size={16} weight="bold" />
                      <span>{label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
            )}

            {/* 4) Dashboard & Treasury menu */}
            {canSee("treasury") && (
            <div className="pt-4 mt-2 border-t border-white/10" data-testid="treasury-group">
              <button
                onClick={toggleTreasury}
                data-testid="treasury-group-toggle"
                className="w-full flex items-center justify-between px-3 py-2 text-[10px] tracking-[0.2em] uppercase font-bold text-gray-400 hover:text-white"
              >
                <span className="flex items-center gap-2">
                  <ChartLine size={14} weight="fill" className="text-amber-400" /> Dashboard &amp; Treasury
                </span>
                <CaretDown size={12} weight="bold" className={`transition-transform ${treasuryOpen ? "" : "-rotate-90"}`} />
              </button>
              {treasuryOpen && (
                <div className="mt-1 space-y-0.5" data-testid="treasury-group-items">
                  {NAV.map(({ to, label, icon: Icon, testid }) => (
                    <NavLink key={to} to={to} data-testid={testid} className={navItemClass} onClick={closeMobile}>
                      <Icon size={18} weight="bold" />
                      <span>{label}</span>
                    </NavLink>
                  ))}
                </div>
              )}
            </div>
            )}
          </nav>
          <div className="border-t border-white/10 p-4">
            <div className="text-[10px] tracking-[0.2em] uppercase text-gray-400 mb-1">User</div>
            <div className="text-sm font-semibold truncate text-white" data-testid="user-email">{user?.email}</div>
            <div className="mt-1 inline-block text-[9px] tracking-[0.2em] uppercase font-bold border border-white/20 text-gray-300 px-1.5 py-0.5 rounded" data-testid="user-role">{user?.role || "user"}</div>
            <button
              data-testid="open-oracle-profile-btn"
              onClick={() => { closeMobile(); setSetupOpen(true); }}
              className="mt-3 w-full flex items-center justify-center gap-2 bg-[#2563EB] text-white px-3 py-2 text-xs font-semibold uppercase tracking-wider rounded-md hover:bg-[#1D4ED8]"
            >
              <PlugsConnected size={14} weight="bold" /> Oracle Profile
            </button>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button
                data-testid="open-change-password-btn"
                onClick={() => { closeMobile(); setPwOpen(true); }}
                className="col-span-2 flex items-center justify-center gap-2 border border-white/20 text-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider rounded-md hover:bg-white/[0.06]"
              >
                <LockKey size={13} weight="bold" /> Change password
              </button>
              <button
                data-testid="theme-toggle-btn"
                onClick={toggleTheme}
                title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                className="flex items-center justify-center gap-2 border border-white/20 text-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider rounded-md hover:bg-white/[0.06]"
              >
                {theme === "dark"
                  ? <><Sun size={13} weight="bold" data-testid="theme-icon-sun" /> Light</>
                  : <><Moon size={13} weight="bold" data-testid="theme-icon-moon" /> Dark</>}
              </button>
              <button
                data-testid="logout-btn"
                onClick={async () => { closeMobile(); await logout(); nav("/login"); }}
                className="flex items-center justify-center gap-2 border border-white/20 text-gray-200 px-2 py-2 text-[11px] font-semibold uppercase tracking-wider rounded-md hover:bg-white/[0.06]"
              >
                <SignOut size={13} weight="bold" /> Sign out
              </button>
            </div>
          </div>
        </aside>

        {/* Content */}
        <main className="flex-1 min-w-0 overflow-auto">
          {children}
        </main>
      </div>

      {/* ERP modals (global) */}
      <ProfileSetupModal />
      <ChangePasswordModal open={pwOpen} onClose={() => setPwOpen(false)} />
      <AgentRunModal />
      <CommandPalette open={paletteOpen} setOpen={setPaletteOpen} canSee={canSee} isProductAdmin={isProductAdmin} />
    </div>
  );
}
